import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PageWrapper from '../PageWrapper/PageWrapper';
import { PermissionFlags } from '../../components/PermissionFlags/PermissionFlags';
import { TabV2, TabsContent, TabsList, TabsTrigger } from '../../components/TabV2/TabV2';
import { Users, BarChart3 } from 'lucide-react';
import SectionTitle from '../../components/SectionTitle/SectionTitle';
import BarChart from '../../components/BarChart/BarChart';
import PieChart from '../../components/PieChart/PieChart';
import NoData from '../../components/NoData/NoData';
import CustomersCrmStatisticsAPI from '../../api/CustomersCrmStatisticsAPI';
import CustomersCrmTotalCard from './components/CustomersCrmTotalCard';
import { inboundChannelToTranslationKeys } from '../../shared/inboundChannelToTranslationKey';

type TimeRange = { start: string; end: string };

const toISODate = (d: Date) => {
  const dd = new Date(d);
  dd.setHours(0, 0, 0, 0);
  return dd.toISOString();
};

function CustomersCrmStatisticsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'customers'>('customers');

  const monthStart = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const monthEnd = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1, 0);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [timeRange, setTimeRange] = useState<TimeRange>({ start: toISODate(monthStart), end: toISODate(monthEnd) });

  const onDateChange = (key: 'start' | 'end', value: string) => {
    const date = new Date(value);
    setTimeRange((prev) => ({ ...prev, [key]: toISODate(date) }));
  };

  const startInputValue = useMemo(() => new Date(timeRange.start).toISOString().slice(0, 10), [timeRange.start]);
  const endInputValue = useMemo(() => new Date(timeRange.end).toISOString().slice(0, 10), [timeRange.end]);

  // Data holders
  const [channelsLabels, setChannelsLabels] = useState<string[]>([]);
  const [channelsValues, setChannelsValues] = useState<number[]>([]);
  const [overTimeLabels, setOverTimeLabels] = useState<string[]>([]);
  const [overTimeValues, setOverTimeValues] = useState<number[]>([]);

  // Fetch inbound channels
  useEffect(() => {
    let mounted = true;
    CustomersCrmStatisticsAPI.getInboundChannels({ startTime: timeRange.start, endTime: timeRange.end })
      .then(({ data }) => {
        if (!mounted) return;
        const rawLabels = data.labels || [];
        const translatedLabels = rawLabels.map(label => t(inboundChannelToTranslationKeys(label)));
        setChannelsLabels(translatedLabels);
        setChannelsValues(data.values || []);
      })
      .catch(() => {
        if (!mounted) return;
        setChannelsLabels([]);
        setChannelsValues([]);
      });
    return () => { mounted = false };
  }, [timeRange.start, timeRange.end, t]);

  // Fetch customers over time
  useEffect(() => {
    let mounted = true;
    CustomersCrmStatisticsAPI.getTotalOverTime({ startTime: timeRange.start, endTime: timeRange.end })
      .then(({ data }) => {
        if (!mounted) return;
        setOverTimeLabels(data.labels || []);
        setOverTimeValues(data.values || []);
      })
      .catch(() => {
        if (!mounted) return;
        setOverTimeLabels([]);
        setOverTimeValues([]);
      });
    return () => { mounted = false };
  }, [timeRange.start, timeRange.end]);

  return (
    <PageWrapper>
      <PermissionFlags requiredFlags={['see-employees']}>
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              <h1 className="text-2xl font-bold text-foreground">{t('stats.customers_crm') || 'CRM Customers Insights'}</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">{t('stats.filters.from') || 'From'}</span>
                <input type="date" value={startInputValue} onChange={(e) => onDateChange('start', e.target.value)} className="px-3 py-1 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">{t('stats.filters.to') || 'To'}</span>
                <input type="date" value={endInputValue} onChange={(e) => onDateChange('end', e.target.value)} className="px-3 py-1 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground" />
              </div>
            </div>
          </div>

          <TabV2 value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="space-y-4">
            <TabsList className="grid w-full grid-cols-1">
              <TabsTrigger value="customers" className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                {t('stats.customers') || 'Customers'}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="customers" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <SectionTitle text={t('stats.customers_total') || 'Customers total'} />
                  <div className="bg-card rounded-lg border border-input p-4">
                    <CustomersCrmTotalCard timeRange={timeRange} />
                  </div>
                </div>
                <div className="space-y-4">
                  <SectionTitle text={t('stats.inbound_channels') || 'Inbound channels'} />
                  {channelsLabels.length ? (
                    <PieChart
                      title={t('stats.inbound_channels') || 'Inbound channels'}
                      labels={channelsLabels}
                      data={channelsValues}
                    />
                  ) : (
                    <NoData message={t('global.empty_results')} />
                  )}
                </div>

                <div className="space-y-4">
                  <SectionTitle text={t('stats.total_over_time') || 'Total over time'} />
                  {overTimeLabels.length ? (
                    <BarChart
                      title={t('stats.total_over_time') || 'Total over time'}
                      xLabels={overTimeLabels}
                      dataSets={[{ label: t('stats.customers') || 'Customers', data: overTimeValues, backgroundColor: ['rgba(16, 185, 129, 0.6)'], borderColor: ['rgba(16, 185, 129, 0.6)'], borderWidth: 1 }]}
                    />
                  ) : (
                    <NoData message={t('global.empty_results')} />
                  )}
                </div>
              </div>
            </TabsContent>
          </TabV2>
        </div>
      </PermissionFlags>
    </PageWrapper>
  );
}

export default CustomersCrmStatisticsPage;


